import {
  useState,
  useEffect,
  useRef,
} from "react";

import {
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

import {
  CheckCircle,
} from "lucide-react";

import {
  useTranslation,
} from "react-i18next";

import logo from "../assets/logo_basic.png";
import AppFooter from "../components/AppFooter";

import {
  supabase,
} from "../supabaseClient";
import {
  getBlogAttribution,
  trackAttributedEventOnce as trackBlogEventOnce,
} from "../utils/blogAnalytics";
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  "https://api.reignitespirit.com";

// Local development:
// const API_BASE =
//   process.env.REACT_APP_API_BASE ||
//   "http://localhost:8080";

export default function Register() {
  const {
    t,
    i18n,
  } = useTranslation();

  const currentLang =
    i18n.language || "en";

  const isArabic =
    currentLang === "ar";

  const [
    name,
    setName,
  ] = useState("");

  const [
    email,
    setEmail,
  ] = useState("");

  const [
    password,
    setPassword,
  ] = useState("");

  const [
    plan,
    setPlan,
  ] = useState("");

  const [
    registrationSource,
    setRegistrationSource,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    success,
    setSuccess,
  ] = useState(false);

  const navigate =
    useNavigate();

  const [
    searchParams,
  ] = useSearchParams();

  const redirectingRef =
    useRef(false);

  // ==================================================
  // NORMALIZE PLAN
  // ==================================================

  const normalizePlan = (p) => {
    const v = (
      p || ""
    )
      .toString()
      .trim()
      .toLowerCase()
      .replace(/^\/+/, "");

    if (!v) {
      return "";
    }

    if (
      v === "free_calculator" ||
      v ===
        "free_financial_runway_calculator" ||
      v.includes(
        "calculator"
      ) ||
      v.includes(
        "free"
      )
    ) {
      return "free_calculator";
    }

    if (
      v === "deep_audit" ||
      v.includes("deep")
    ) {
      return "deep_audit";
    }

    if (
      v === "snapshot" ||
      v.includes("snap")
    ) {
      return "snapshot";
    }

    return "";
  };

  // ==================================================
  // NORMALIZE REGISTRATION SOURCE
  // ==================================================

  const normalizeSource = (value) => {
  const v = (value || "")
    .toString()
    .trim()
    .toLowerCase();

  if (v === "financial_calculator") {
    return "financial_calculator";
  }

  if (v === "ai_risk_calculator") {
    return "ai_risk_calculator";
  }

  if (
    v === "blog_comment" ||
    v === "comment"
  ) {
    return "blog_comment";
  }

  if (v === "career_audit") {
    return "career_audit";
  }

  // ============================================
  // PHASE 6C - BLOG ATTRIBUTION
  // ============================================

  if (
    v === "blog" ||
    v === "blog_article"
  ) {
    return "blog";
  }

  return "";
};

  // ==================================================
  // READ PLAN + SOURCE FROM LANDING PAGE
  // ==================================================

  useEffect(() => {
    const planFromUrl =
      normalizePlan(
        searchParams.get(
          "plan"
        )
      );

    const sourceFromUrl =
      normalizeSource(
        searchParams.get(
          "source"
        )
      );

    const sourceFromStorage =
      normalizeSource(
        localStorage.getItem(
          "registrationSource"
        )
      );

    const freeToolIntent =
      normalizeSource(
        localStorage.getItem(
          "freeToolIntent"
        )
      );

    const finalSource =
      sourceFromUrl ||
      sourceFromStorage ||
      freeToolIntent;

    if (finalSource) {
      setRegistrationSource(
        finalSource
      );

      localStorage.setItem(
        "registrationSource",
        finalSource
      );

      if (
        finalSource ===
          "financial_calculator" ||
        finalSource ===
          "ai_risk_calculator"
      ) {
        localStorage.setItem(
          "freeToolIntent",
          finalSource
        );
      }
    }

    // URL plan wins
    if (planFromUrl) {
      setPlan(
        planFromUrl
      );

      localStorage.setItem(
        "selectedPlan",
        planFromUrl
      );

      return;
    }

    const storedPlan =
      normalizePlan(
        localStorage.getItem(
          "selectedPlan"
        )
      );

    if (storedPlan) {
      setPlan(
        storedPlan
      );

      return;
    }

    const returnUrl =
      searchParams.get(
        "return"
      );

    if (returnUrl) {
      setPlan(
        "free_calculator"
      );

      localStorage.setItem(
        "selectedPlan",
        "free_calculator"
      );

      return;
    }

    navigate(
      "/landing",
      {
        replace: true,
      }
    );
  }, [
    searchParams,
    navigate,
  ]);

  // ==================================================
  // ROUTE VALUES
  // ==================================================

  const returnUrl =
    searchParams.get(
      "return"
    );

  const source =
    normalizeSource(
      searchParams.get(
        "source"
      )
    ) ||
    registrationSource;

  const isCommentRegister =
    source ===
      "blog_comment" ||
    searchParams.get(
      "source"
    ) === "comment";

  const isAiRiskCalculator =
    plan ===
      "free_calculator" &&
    source ===
      "ai_risk_calculator";

  const isFinancialCalculator =
    plan ===
      "free_calculator" &&
    !isAiRiskCalculator;

  // ==================================================
  // LOGIN BUTTON FOR EXISTING USERS
  // ==================================================

  const handleExistingUserLogin =
    () => {
      /*
        The important part:

        Regardless of which free tool
        brought the user here, an
        existing registered user should
        login and land on Dashboard.
      */

      localStorage.setItem(
        "redirect_after_login",
        "/dashboard"
      );

      if (source) {
        localStorage.setItem(
          "registrationSource",
          source
        );
      }

      if (
        source ===
          "ai_risk_calculator" ||
        source ===
          "financial_calculator"
      ) {
        localStorage.setItem(
          "freeToolIntent",
          source
        );
      }

      navigate(
        `/login?return=${encodeURIComponent(
          "/dashboard"
        )}`,
        {
          replace: false,
        }
      );
    };

  // ==================================================
  // VALIDATION
  // ==================================================

  const validate = () => {
    if (!name.trim()) {
      return (
        t("auth.fullName") +
        " is required"
      );
    }

    if (!email.trim()) {
      return (
        t("auth.email") +
        " is required"
      );
    }

    if (
      !password.trim()
    ) {
      return (
        t("auth.password") +
        " is required"
      );
    }

    if (
      password.length < 6
    ) {
      return "Password must be at least 6 characters";
    }

    return "";
  };

  // ==================================================
  // EMAIL/PASSWORD REGISTRATION
  // ==================================================

  const handleRegister =
    async (e) => {
      e.preventDefault();

if (redirectingRef.current) {
  return;
}

setError("");

const validationError =
  validate();

if (validationError) {
  setError(validationError);
  return;
}

setLoading(true);

try {
  // ============================================
  // PHASE 6C - READ ORIGINAL BLOG ATTRIBUTION
  // ============================================

  const blogAttribution =
    getBlogAttribution();       const rawPlan =
          normalizePlan(
            plan
          ) ||
          normalizePlan(
            localStorage.getItem(
              "selectedPlan"
            )
          ) ||
          "snapshot";

        const storedSource =
          normalizeSource(
            registrationSource
          ) ||
          normalizeSource(
            localStorage.getItem(
              "registrationSource"
            )
          ) ||
          normalizeSource(
            localStorage.getItem(
              "freeToolIntent"
            )
          );

        let finalSource =
  "career_audit";

// ============================================
// PHASE 6C
// User originally arrived from a blog article
// ============================================

if (
  blogAttribution?.blogSlug
) {
  finalSource =
    "blog";
}

// Blog comment registration remains more specific.
if (
  isCommentRegister
) {
  finalSource =
    "blog_comment";
}
else if (
  rawPlan ===
  "free_calculator"
) {
  finalSource =
    storedSource ||
    "financial_calculator";
}

        localStorage.setItem(
          "selectedPlan",
          rawPlan
        );

        localStorage.setItem(
          "registrationSource",
          finalSource
        );

        if (
          finalSource ===
            "financial_calculator" ||
          finalSource ===
            "ai_risk_calculator"
        ) {
          localStorage.setItem(
            "freeToolIntent",
            finalSource
          );

          localStorage.setItem(
            "redirect_after_login",
            "/dashboard"
          );
        }

        if (returnUrl) {
          localStorage.setItem(
            "redirect_after_login",
            returnUrl
          );

          if (
            isCommentRegister
          ) {
            localStorage.setItem(
              "comment_register_return",
              returnUrl
            );
          }
        }

        const res =
          await fetch(
            `${API_BASE}/api/users/register`,
            {
              method:
                "POST",

              headers: {
                "Content-Type":
                  "application/json",
              },

              body:
                JSON.stringify(
                  {
                    Name:
                      name,

                    Email:
                      email,

                    Password:
                      password,

                    Plan:
                      rawPlan,

                    IsPremium:
                      rawPlan ===
                      "deep_audit",

                    Source:
                      finalSource,
                  }
                ),
            }
          );

        let data = null;

        try {
          data =
            await res.json();
        } catch {
          data = null;
        }

        console.log(
          "Backend register response:",
          data
        );

        // -----------------------------------------------
        // ALREADY REGISTERED
        // -----------------------------------------------

        if (!res.ok) {
          if (
            res.status ===
            409
          ) {
            /*
              Don't immediately hide the
              reason from the user.

              Show message + automatically
              redirect shortly afterwards.

              They can also use the visible
              Login button below the form.
            */

            localStorage.setItem(
              "redirect_after_login",
              "/dashboard"
            );

            if (
              finalSource
            ) {
              localStorage.setItem(
                "registrationSource",
                finalSource
              );
            }

            setError(
              "This email is already registered. Please click Login below to access your Free Tools Dashboard."
            );

            return;
          }

          throw new Error(
            data?.message ||
              "Backend registration failed"
          );
        }

        // -----------------------------------------------
        // REGISTERED → SIGN IN
        // -----------------------------------------------

        const {
          data: loginData,
          error:
            loginError,
        } =
          await supabase.auth
            .signInWithPassword({
              email,
              password,
            });

        if (
          loginError
        ) {
          const msg = (
            loginError.message ||
            ""
          ).toLowerCase();

          if (
            msg.includes(
              "email not confirmed"
            )
          ) {
            setError(
              "Please go to your inbox and confirm your email."
            );

            return;
          }

          throw loginError;
        }

        const authUid =
          loginData.user.id;

        sessionStorage.setItem(
          "user_plan",
          rawPlan
        );

        localStorage.setItem(
          `user_plan_${authUid}`,
          rawPlan
        );

        let dbUser =
          null;

        for (
          let i = 0;
          i < 6;
          i++
        ) {
          const {
            data:
              userData,
          } =
            await supabase
              .from(
                "users"
              )
              .select(
                "id,name,is_premium,can_bypass_payment,plan,source"
              )
              .eq(
                "auth_uid",
                authUid
              )
              .maybeSingle();

          if (
            userData
          ) {
            dbUser =
              userData;

            break;
          }

          await new Promise(
            (resolve) =>
              setTimeout(
                resolve,
                500
              )
          );
        }

        if (!dbUser) {
          throw new Error(
            "User created in Auth but missing in DB"
          );
        }
// ============================================
// PHASE 6C
// Successful blog-attributed registration
//
// IMPORTANT:
// Only track after:
// 1. backend user creation succeeded
// 2. Supabase login succeeded
// 3. public.users row exists
// ============================================

if (
  blogAttribution?.blogSlug
) {
  trackBlogEventOnce(
    `signup_from_blog_${authUid}`,

    "signup_from_blog",

    {
      ...blogAttribution,

      reportType:
        rawPlan === "deep_audit"
          ? "deep_audit"
          : rawPlan === "snapshot"
          ? "snapshot"
          : "",
    }
  );
}
        sessionStorage.setItem(
          "db_verified",
          "1"
        );

        localStorage.setItem(
          "onboardingLock",
          "true"
        );

        redirectingRef.current =
          true;

        setSuccess(
          true
        );

        const savedReturn =
          localStorage.getItem(
            "redirect_after_login"
          );

        const nextRoute =
          rawPlan ===
          "free_calculator"
            ? "/dashboard"
            : savedReturn ||
              "/instructions";

        localStorage.removeItem(
          "redirect_after_login"
        );

        setTimeout(
          () =>
            navigate(
              nextRoute,
              {
                replace:
                  true,
              }
            ),
          800
        );
      } catch (err) {
        redirectingRef.current =
          false;

        setError(
          err?.message ||
            "Registration failed"
        );
      } finally {
        setLoading(
          false
        );
      }
    };

  // ==================================================
  // GOOGLE REGISTRATION
  // ==================================================

  const handleGoogleRegister =
    async (e) => {
      e?.preventDefault?.();

      const storedPlan =
        normalizePlan(
          plan
        ) ||
        normalizePlan(
          localStorage.getItem(
            "selectedPlan"
          )
        );

      if (!storedPlan) {
        navigate(
          "/landing#pricing",
          {
            replace: true,
          }
        );

        return;
      }

      const sourceFromUrl =
        normalizeSource(
          searchParams.get(
            "source"
          )
        );

      const sourceFromState =
        normalizeSource(
          registrationSource
        );

      const sourceFromStorage =
        normalizeSource(
          localStorage.getItem(
            "registrationSource"
          )
        );

      const toolIntent =
        normalizeSource(
          localStorage.getItem(
            "freeToolIntent"
          )
        );

      let finalSource =
        "career_audit";

      if (
        isCommentRegister
      ) {
        finalSource =
          "blog_comment";
      } else if (
        storedPlan ===
        "free_calculator"
      ) {
        finalSource =
          sourceFromUrl ||
          sourceFromState ||
          sourceFromStorage ||
          toolIntent ||
          "financial_calculator";
      }

      localStorage.setItem(
        "oauth_flow",
        "register"
      );

      localStorage.setItem(
        "selectedPlan",
        storedPlan
      );

      localStorage.setItem(
        "oauth_register_plan",
        storedPlan
      );

      localStorage.setItem(
        "registrationSource",
        finalSource
      );

      if (
        finalSource ===
          "financial_calculator" ||
        finalSource ===
          "ai_risk_calculator"
      ) {
        localStorage.setItem(
          "freeToolIntent",
          finalSource
        );

        localStorage.setItem(
          "redirect_after_login",
          "/dashboard"
        );
      }

      if (returnUrl) {
        localStorage.setItem(
          "redirect_after_login",
          returnUrl
        );
      }

      const callbackParams =
        new URLSearchParams();

      callbackParams.set(
        "flow",
        "register"
      );

      callbackParams.set(
        "plan",
        storedPlan
      );

      callbackParams.set(
        "source",
        finalSource
      );

      if (returnUrl) {
        callbackParams.set(
          "return",
          returnUrl
        );
      }

      const redirectUrl =
        `${window.location.origin}/auth/callback?${callbackParams.toString()}`;

      await supabase.auth
        .signInWithOAuth({
          provider:
            "google",

          options: {
            redirectTo:
              redirectUrl,

            queryParams: {
              prompt:
                "select_account",
            },
          },
        });
    };

  // ==================================================
  // DISPLAY COPY
  // ==================================================

  const freeToolTitle =
    isAiRiskCalculator
      ? "AI Career Risk Calculator - Free use tool"
      : "Financial Runway Calculator - Free use tool";

  const freeToolDescription =
    isAiRiskCalculator
      ? "Understand how AI and automation may affect your current role."
      : "Estimate your financial runway before making a career transition.";

  // ==================================================
  // UI
  // ==================================================

  return (
  <div
    dir={isArabic ? "rtl" : "ltr"}
    className="min-h-screen flex flex-col bg-[#0F0A24] text-white"
  >
    {/* =====================================================
        AUTH HEADER
    ===================================================== */}
    <header className="border-b border-[#EB6AB5]/20 bg-[#11111f]/95 backdrop-blur">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between gap-4">

        {/* LOGO / BRAND */}
        <button
          type="button"
          onClick={() => navigate("/landing")}
          className="flex items-center gap-3 flex-shrink-0"
          aria-label="Go to Reignite Spirit home page"
        >
          <img
            src={logo}
            alt="Reignite Spirit"
            className="w-11 h-11 object-contain drop-shadow-[0_0_12px_#eb6fbd]"
          />

          <div className="text-left hidden sm:block">
            <div className="text-[#EB6AB5] text-lg font-bold tracking-wide">
              REIGNITE.SPIRIT
            </div>

            <div className="text-[#EB6AB5]/70 text-xs tracking-[0.16em]">
              REKINDLE PURPOSE
            </div>
          </div>
        </button>

        {/* HEADER ACTIONS */}
        <div className="flex items-center gap-3">

          {/* BACK TO PLAN SELECTION */}
          <button
            type="button"
            onClick={() =>
              navigate({
                pathname: "/landing",
                hash: "#pricing",
              })
            }
            className="hidden sm:inline-flex px-4 py-2 rounded-lg border border-white/20 text-white hover:bg-white/10 transition whitespace-nowrap"
          >
            Back to Plans
          </button>

          {/* EXISTING USER LOGIN */}
          <button
            type="button"
            onClick={handleExistingUserLogin}
            className="px-4 py-2 rounded-lg bg-[#cb01bd] hover:bg-[#e94ce0] text-white font-bold transition whitespace-nowrap"
          >
            Login
          </button>
        </div>
      </div>
    </header>

    {/* =====================================================
        REGISTER CONTENT
    ===================================================== */}
    <main className="flex-1 flex items-center justify-center px-4 py-12">
      <form
        onSubmit={handleRegister}
        className="
          bg-[#11111f]
          border
          border-[#EB6AB5]/30
          p-7
          sm:p-10
          rounded-3xl
          w-full
          max-w-md
          shadow-2xl
        "
      >
        {/* =========================================
            BRAND
        ========================================= */}
        <div className="text-center mb-6">
          <img
            src={logo}
            alt="Reignite Spirit Logo"
            className="w-20 mx-auto mb-4 drop-shadow-[0_0_12px_#eb6fbd]"
          />

          <h1 className="text-3xl text-[#EB6AB5]">
            REIGNITE.SPIRIT
          </h1>
        </div>

        {/* =========================================
            PLAN / FREE TOOL
        ========================================= */}
        {plan && (
          <div className="text-center text-sm text-gray-400 mb-6">
            {plan === "free_calculator" ? (
              <>
                {/* FREE TOOL TITLE */}
                <p className="text-[#EB6AB5] font-semibold text-base">
                  {freeToolTitle}
                </p>

                {/* PRIVACY MESSAGE */}
                <p className="mt-2 text-sm font-bold text-white leading-relaxed">
                  Private by design. Your inputs are never visible to us
                </p>

                {/* FREE TOOL DESCRIPTION */}
                <p className="text-gray-400 text-xs mt-2 leading-relaxed">
                  {freeToolDescription}
                </p>

                {/* FINANCIAL RUNWAY FAQ */}
                {isFinancialCalculator && (
                  <button
                    type="button"
                    onClick={() =>
                      navigate("/Financial_Runway_Calculator_FAQ")
                    }
                    className="
                      mt-2
                      text-xs
                      text-[#EB6AB5]
                      underline
                      underline-offset-4
                      hover:text-white
                      transition
                    "
                  >
                    Financial Runway Calculator FAQ
                  </button>
                )}
              </>
            ) : (
              <p>
                Selected Plan:{" "}
                <span className="text-[#EB6AB5] font-semibold">
                  {plan === "snapshot"
                    ? "Snapshot"
                    : plan === "deep_audit"
                    ? "Deep Audit"
                    : plan}
                </span>
              </p>
            )}
          </div>
        )}

        {/* =========================================
            FULL NAME
        ========================================= */}
        <input
          type="text"
          placeholder={t("auth.fullName")}
          value={name}
          onChange={(e) =>
            setName(e.target.value)
          }
          className="
            w-full
            p-3
            mb-3
            rounded-lg
            bg-[#11111f]
            border
            border-[#EB6AB5]/30
            outline-none
            focus:border-[#EB6AB5]
            transition
          "
        />

        {/* =========================================
            EMAIL
        ========================================= */}
        <input
          type="email"
          placeholder={t("auth.email")}
          value={email}
          onChange={(e) =>
            setEmail(e.target.value)
          }
          className="
            w-full
            p-3
            mb-3
            rounded-lg
            bg-[#11111f]
            border
            border-[#EB6AB5]/30
            outline-none
            focus:border-[#EB6AB5]
            transition
          "
        />

        {/* =========================================
            PASSWORD
        ========================================= */}
        <input
          type="password"
          placeholder={t("auth.password")}
          value={password}
          onChange={(e) =>
            setPassword(e.target.value)
          }
          className="
            w-full
            p-3
            mb-4
            rounded-lg
            bg-[#11111f]
            border
            border-[#EB6AB5]/30
            outline-none
            focus:border-[#EB6AB5]
            transition
          "
        />

        {/* =========================================
            ERROR
        ========================================= */}
        {error && (
          <div className="mb-4 rounded-xl border border-red-400/30 bg-red-500/10 p-3">
            <p className="text-red-300 text-sm">
              ⚠ {error}
            </p>
          </div>
        )}

        {/* =========================================
            REGISTER BUTTON
        ========================================= */}
        <button
          type="submit"
          disabled={loading}
          className="
            w-full
            py-3
            bg-[#EB6AB5]
            hover:bg-[#ff83d1]
            disabled:opacity-60
            disabled:cursor-not-allowed
            text-black
            font-semibold
            rounded-full
            transition
          "
        >
          {loading
            ? t("auth.registering")
            : t("auth.registerButton")}
        </button>

        {/* =========================================
            EXISTING ACCOUNT
        ========================================= */}
        <div className="mt-5 text-center">
          <p className="text-sm text-gray-400">
            Already registered?
          </p>

          <button
            type="button"
            onClick={handleExistingUserLogin}
            className="
              mt-2
              text-[#EB6AB5]
              hover:text-white
              font-semibold
              underline
              underline-offset-4
              transition
            "
          >
            Login to your Dashboard
          </button>
        </div>

        {/* =========================================
            OR
        ========================================= */}
        <div className="mt-5">
          <div className="flex items-center gap-3 my-4">
            <div className="h-px flex-1 bg-white/10" />

            <span className="text-xs text-gray-400">
              OR
            </span>

            <div className="h-px flex-1 bg-white/10" />
          </div>

          {/* =======================================
              GOOGLE REGISTER
          ======================================= */}
          <button
            type="button"
            onClick={handleGoogleRegister}
            disabled={loading}
            className="
              w-full
              py-3
              rounded-full
              border
              border-white/15
              bg-[#0f0a24]
              hover:bg-white/5
              disabled:opacity-60
              transition
              flex
              items-center
              justify-center
              gap-3
            "
          >
            <img
              src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
              alt="Google"
              className="w-5 h-5"
            />

            <span className="text-white font-medium">
              Continue with Google
            </span>
          </button>
        </div>

        {/* =========================================
            CHANGE PLAN / BACK TO PRICING

            For paid plans we also give the visitor
            a visible way to change Snapshot/Deep Audit.
        ========================================= */}
        {plan !== "free_calculator" && (
          <button
            type="button"
            onClick={() =>
              navigate({
                pathname: "/landing",
                hash: "#pricing",
              })
            }
            className="
              block
              mx-auto
              mt-6
              text-xs
              text-gray-500
              hover:text-[#EB6AB5]
              transition
            "
          >
            ← Change selected plan
          </button>
        )}

        {/* =========================================
            RETURN TO FREE TOOLS
        ========================================= */}
        {plan === "free_calculator" && (
          <button
            type="button"
            onClick={() =>
              navigate("/landing#free-tools")
            }
            className="
              block
              mx-auto
              mt-5
              text-xs
              text-gray-500
              hover:text-[#EB6AB5]
              transition
            "
          >
            ← Back to Free Tools
          </button>
        )}
      </form>
    </main>

    {/* =====================================================
        SAME FOOTER AS LOGIN / FAQ / PUBLIC PAGES
    ===================================================== */}
    <AppFooter
      variant="landing"
      logoSrc={logo}
    />

    {/* =====================================================
        SUCCESS MODAL
    ===================================================== */}
    <AnimatePresence>
      {success && (
        <motion.div
          className="
            fixed
            inset-0
            flex
            items-center
            justify-center
            bg-black/60
            z-[9999]
            px-4
          "
          initial={{
            opacity: 0,
          }}
          animate={{
            opacity: 1,
          }}
          exit={{
            opacity: 0,
          }}
        >
          <motion.div
            className="
              bg-[#11111f]
              border
              border-[#EB6AB5]/30
              p-10
              rounded-3xl
              text-center
              shadow-2xl
            "
            initial={{
              scale: 0.95,
            }}
            animate={{
              scale: 1,
            }}
          >
            <CheckCircle className="text-[#EB6AB5] w-14 h-14 mx-auto mb-3" />

            <p className="text-[#EB6AB5] font-semibold">
              Registration successful 🎉
            </p>

            <p className="text-gray-400 text-sm mt-2">
              Redirecting...
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);
}